import {
  Box,
  Collapse,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { EChartsOption } from 'echarts';
import { useEffect, useMemo, useState } from 'react';

import { GEO_SCALE, PALETTE, ensureWorldMap } from 'entities/analytics/lib';
import { useGeography } from 'entities/analytics/model';
import { AnalyticsCard } from 'entities/analytics/ui/analytics-card';
import { EChart } from 'entities/analytics/ui/echart';

import { GeographyMetric, GeographyRow } from 'shared/api';

const METRICS: Array<{ value: GeographyMetric; label: string }> = [
  { value: 'users', label: 'Users' },
  { value: 'people', label: 'People' },
  { value: 'pets', label: 'Pets' },
];

const TOP_N = 10;

const METRIC_LABEL: Record<GeographyMetric, string> = {
  users: 'Users',
  people: 'People',
  pets: 'Pets',
};

// The backend returns ISO 3166 official names; the bundled world GeoJSON keys
// features by short names. Translate the known divergences so choropleth
// regions match (otherwise a country stays uncoloured and its tooltip is NaN).
const GEO_NAME_MAP: Record<string, string> = {
  'Russian Federation': 'Russia',
  'Moldova, Republic of': 'Moldova',
  'Korea, Republic of': 'Korea',
  'Iran, Islamic Republic of': 'Iran',
  'United Republic of Tanzania': 'Tanzania',
  'Czech Republic': 'Czech Rep.',
  'Antigua and Barbuda': 'Antigua and Barb.',
  'Dominican Republic': 'Dominican Rep.',
};

const toGeoName = (name: string): string => GEO_NAME_MAP[name] ?? name;

export const GeographyTable = ({ rows, open }: { rows: GeographyRow[]; open: boolean }) => (
  <Collapse in={open} unmountOnExit>
    <Box sx={{ overflowX: 'auto', mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Country</TableCell>
            <TableCell align="right">Users</TableCell>
            <TableCell align="right">People</TableCell>
            <TableCell align="right">Pets</TableCell>
            <TableCell align="right">Dogs</TableCell>
            <TableCell align="right">Cats</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.code ?? `none-${i}`}>
              <TableCell>{r.name}</TableCell>
              <TableCell align="right">{r.users.toLocaleString()}</TableCell>
              <TableCell align="right">{r.people.toLocaleString()}</TableCell>
              <TableCell align="right">{r.pets.toLocaleString()}</TableCell>
              <TableCell align="right">{r.dogs.toLocaleString()}</TableCell>
              <TableCell align="right">{r.cats.toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </Collapse>
);

export const GeographyCard = () => {
  const { data, isLoading, isFetching } = useGeography();
  const [metric, setMetric] = useState<GeographyMetric>('users');
  const [tableOpen, setTableOpen] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let active = true;
    ensureWorldMap()
      .then(() => {
        if (active) setMapReady(true);
      })
      .catch(() => {
        // Map asset failed to load — the bars + table still render; the map
        // area stays a placeholder rather than crashing the card.
      });
    return () => {
      active = false;
    };
  }, []);

  const rows = useMemo<GeographyRow[]>(() => data?.data.series ?? [], [data]);

  const topRows = useMemo(
    () =>
      [...rows]
        .filter((r) => r.code)
        .sort((a, b) => b[metric] - a[metric])
        .slice(0, TOP_N),
    [rows, metric],
  );

  const maxValue = useMemo(() => Math.max(1, ...rows.filter((r) => r.code).map((r) => r[metric])), [rows, metric]);

  // EChartsOption does not type map-specific fields (roam, zoom, center,
  // areaColor, calculable) — useMemo<EChartsOption> annotates the variable
  // rather than using an `as` cast, satisfying consistent-type-assertions.
  const mapOption = useMemo<EChartsOption>(
    () => ({
      tooltip: {
        trigger: 'item',
        // TopLevelFormatterParams is a broad union incompatible with the map-specific
        // callback shape; eslint-disable covers the any needed for the formatter.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: ((p: { name?: string; value?: unknown }) => {
          // ECharts assigns NaN to map regions with no matching data point.
          const v = Number(p.value);
          const shown = Number.isFinite(v) ? v.toLocaleString() : 'no data';
          return `${p.name ?? ''}<br/>${METRIC_LABEL[metric]}: ${shown}`;
        }) as any,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      visualMap: {
        left: 6,
        bottom: 6,
        min: 0,
        max: maxValue,
        calculable: true,
        inRange: { color: [...GEO_SCALE] },
        textStyle: { fontSize: 10, color: PALETTE.axis },
      } as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      series: [
        {
          type: 'map',
          map: 'world',
          roam: false,
          zoom: 1.2,
          center: [10, 25],
          itemStyle: { borderColor: '#d7dedc', borderWidth: 0.5, areaColor: '#f4f7f6' },
          emphasis: { itemStyle: { areaColor: PALETTE.amber }, label: { show: false } },
          data: rows.filter((r) => r.code).map((r) => ({ name: toGeoName(r.name), value: r[metric] })),
        },
      ] as any,
    }),
    [rows, metric, maxValue],
  );

  const barOption = useMemo<EChartsOption>(
    () => ({
      grid: { left: 8, right: 28, top: 8, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: topRows.map((r) => r.name),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: '#40514d', fontSize: 12 },
      },
      series: [
        {
          type: 'bar',
          data: topRows.map((r) => r[metric]),
          barWidth: 13,
          itemStyle: { color: PALETTE.primary, borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right',
            color: PALETTE.axis,
            fontSize: 11,
            // CallbackDataParams.value is a union; cast to number for bar series
            formatter: (p: { value?: unknown }) => Number(p.value ?? 0).toLocaleString(),
          },
        },
      ],
    }),
    [topRows, metric],
  );

  return (
    <Box sx={{ mb: 2 }}>
      <AnalyticsCard
        title="Geography"
        subtitle="Distribution by country · metric colors both map and bars"
        rightAction={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, v) => v && setMetric(v as GeographyMetric)}
          >
            {METRICS.map(({ value, label }) => (
              <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={!isLoading && rows.length === 0}
        emptyMessage="No data"
        footer={
          <Link component="button" type="button" underline="hover" onClick={() => setTableOpen((o) => !o)}>
            {tableOpen ? 'Hide table' : 'Show full table'}
          </Link>
        }
        chartHeight={320}
        chart={
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1.55fr 1fr' },
              height: '100%',
            }}
          >
            {mapReady ? <EChart option={mapOption} height={320} /> : <Box sx={{ height: 320 }} />}
            <EChart option={barOption} height={320} />
          </Box>
        }
      />
      <GeographyTable rows={[...rows].sort((a, b) => b[metric] - a[metric])} open={tableOpen} />
    </Box>
  );
};
